// Global WebSocket service for job updates - shared singleton
import { toast } from 'react-toastify';
import { JobData } from 'screens/JobPaste/hooks/useJobPasteWebSocket';

class GlobalWebSocketService {
  private static instance: GlobalWebSocketService | null = null;
  private ws: WebSocket | null = null;
  private isConnecting = false;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 5;
  private readonly reconnectDelay = 3000;
  private messageHandlers: Set<(message: any) => void> = new Set();
  private heartbeatInterval: NodeJS.Timeout | null = null; // Heartbeat interval for connection health
  private isAuthenticated = false; // Flag to track authentication status
  private userStore: any = null; // Store reference to UserStore instance
  private environment = import.meta.env.VITE_ENVIRONMENT;

  private constructor() {}

  static getInstance(): GlobalWebSocketService {
    if (!GlobalWebSocketService.instance) {
      GlobalWebSocketService.instance = new GlobalWebSocketService();
    }
    return GlobalWebSocketService.instance;
  }

  // Method to set the UserStore instance
  setUserStore(userStore: any): void {
    this.userStore = userStore; // Store the reference directly, not a copy
  }

  private getWebSocketUrl(): string {
    let backendUrl;
    
    if (import.meta.env.VITE_MODE === 'dev') {
      backendUrl = import.meta.env.VITE_BACKEND_SOCKET_ENDPOINT;
    } else {
      backendUrl = (window as any).config?.backendSocketEndpoint || (window as any).config?.backendBaseUrl;
    }
    
    if (!backendUrl) {
      throw new Error('WebSocket backend URL not configured');
    }
    
    if (backendUrl?.endsWith('/')) {
      backendUrl = backendUrl.slice(0, -1);
    }
    
    const wsUrl = backendUrl?.replace('https://', 'wss://').replace('http://', 'ws://');
    const fullUrl = `${wsUrl}/ws/job-updates/`;
    
    return fullUrl;
  }

  connect(): WebSocket | null {
    // Check if we already have an active connection
    if (this.ws?.readyState === WebSocket.OPEN) {
      return this.ws;
    }

    // Check if we're already trying to connect
    if (this.isConnecting) {
      return null;
    }

    this.isConnecting = true;

    try {
      this.ws = new WebSocket(this.getWebSocketUrl());

      this.ws.onopen = () => {
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        
        if (this.reconnectTimeout) {
          clearTimeout(this.reconnectTimeout);
          this.reconnectTimeout = null;
        }
        
        // Send "Connection Successful" message
        this.send({ type: 'connection_successful' });
        
        // Start heartbeat to keep connection alive during long periods
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          // Handle connection establishment message
          if (message.type === 'connection_established') {
            // Send user authentication details after connection is established
            this.sendUserAuthenticationDetails();
          }
          
          // Handle authentication response
          if (message.type === 'authentication_response') {
            if (message.authenticated) {
              this.isAuthenticated = true;
            } else {
              this.isAuthenticated = false;
              // Close connection if authentication fails
              this.disconnect();
            }
          }
          
          this.messageHandlers.forEach(handler => {
            try {
              handler(message);
            } catch (error) {
              if (this.environment === 'development') {
                console.error('Error in WebSocket message handler:', error);
              }
              toast.error('Error in WebSocket message handler!');
            }
          });
        } catch (error) {
          if (this.environment === 'development') {
            console.error('Error parsing WebSocket message:', error);
          }
          toast.error('Error parsing WebSocket message!');
        }
      };

      this.ws.onerror = (error) => {
        this.isConnecting = false;
      };

      this.ws.onclose = (event) => {
        this.isConnecting = false;
        this.isAuthenticated = false; // Reset authentication status
        
        // Stop heartbeat on disconnect
        this.stopHeartbeat();
        
        // Only attempt reconnection if it wasn't a normal closure
        if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          
          this.reconnectTimeout = setTimeout(() => {
            this.connect();
          }, this.reconnectDelay);
        }
        
        this.ws = null;
      };

      return this.ws;
    } catch (error) {
      this.isConnecting = false;
      return null;
    }
  }

  /**
   * Start heartbeat to keep connection alive during long periods of inactivity
   * Sends heartbeat messages every 30 seconds to prevent connection timeout
   */
  private startHeartbeat(): void {
    this.stopHeartbeat(); // Clear any existing heartbeat
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected && this.isAuthenticated) {
        this.send({ type: 'heartbeat' });
      }
    }, 30000); // Send heartbeat every 30 seconds
  }

  /**
   * Stop heartbeat interval
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  disconnect() {
    if (this.ws) {
      this.ws.close(1000); // Normal closure
      this.ws = null;
    }
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    // Stop heartbeat
    this.stopHeartbeat();
    
    // Reset authentication status
    this.isAuthenticated = false;
  }

  get isUserAuthenticated(): boolean {
    return this.isAuthenticated;
  }

  send(data: any): boolean {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
      return true;
    }
    return false;
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN || false;
  }

  addMessageHandler(handler: (message: any) => void) {
    this.messageHandlers.add(handler);
  }

  removeMessageHandler(handler: (message: any) => void) {
    this.messageHandlers.delete(handler);
  }

  // Method for sending editing status (still needed for real-time collaboration)
  sendEditingStatus(isEditing: boolean, roomId: string, userId: string) {
    this.send({
      type: 'editing_status',
      isEditing,
      roomId,
      userId,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Send user authentication details to the server
   */
  private sendUserAuthenticationDetails(): void {
    // Check if we have access to UserStore
    if (!this.userStore) {
      this.send({ 
        type: 'authentication_failed', 
        message: 'UserStore not available' 
      });
      return;
    }

    // Check if user data is currently being loaded
    if (this.userStore.isUserInfoLoading) {
      // Retry authentication after 1 second
      setTimeout(() => {
        if (this.isConnected && !this.isAuthenticated) {
          this.sendUserAuthenticationDetails();
        }
      }, 1000);
      
      return;
    }

    // Check if user metadata is available
    if (!this.userStore.userMetadata) {
      // Retry authentication after 2 seconds
      setTimeout(() => {
        if (this.isConnected && !this.isAuthenticated) {
          this.sendUserAuthenticationDetails();
        }
      }, 2000);
      
      return;
    }

    try {
      const userInfo = this.userStore.userMetadata.userInfo;
      
      // Additional check to ensure userInfo exists
      if (!userInfo) {
        setTimeout(() => {
          if (this.isConnected && !this.isAuthenticated) {
            this.sendUserAuthenticationDetails();
          }
        }, 2000);
        
        return;
      }

      const userDetails = {
        type: 'user_authentication',
        user_id: userInfo?.id || '',
        email: userInfo?.email || '',
        name: userInfo?.name || '',
        department: userInfo?.deptId?.departmentCode || '',
        department_name: userInfo?.deptId?.departmentName || '',
        roles: this.userStore.userModulesRoles?.pps || [],
        timestamp: new Date().toISOString()
      };
      this.send(userDetails);
    } catch (error) {
      this.send({ 
        type: 'authentication_failed', 
        message: 'Error preparing user details' 
      });
    }
  }
}

// Export a singleton instance
export const globalWebSocketService = GlobalWebSocketService.getInstance();

// Export the class for type checking
export { GlobalWebSocketService };
